import { Box, FormHelperText } from '@mui/material';
import _ from 'lodash';
import { CSSProperties, useEffect } from 'react';

import {
    AllowedInputValueItem,
    InputState,
    setInputStateIfNotAlready,
} from '../../utils/inputState';
import { arrayWithValue, arrayWithoutValue, includesObject } from '../../utils/iteration';
import { Children, Component, DefiniteComponent, ReturnComponent } from '../../utils/reactTypes';
import { AxleChip, ChipsInRows } from '../displayFields/chips';
import { If } from '../utils/conditionalComponents';
import { HorizontalStack, VerticalStack } from '../utils/linearLayouts';
import { AxleAutocompleteField } from './AxleAutocompleteField';
import { FieldWithAxleLabel } from './AxleLabel';

type AxleAutocompleteMultiselectProps<Option, InputValue extends AllowedInputValueItem> = {
    label?: string;
    description?: string;
    inlineLabel?: boolean;
    enabled?: boolean;
    inputState: InputState<InputValue[]>;
    options: Option[];
    moreOptions?: Option[];
    optionToValue: (option: Option) => InputValue;
    optionToLabel: (option: Option) => string;
    optionToToolTip?: (option: Option) => string;
    renderOption?: (option: Option) => ReturnComponent;
    valueToOption?: (value: InputValue) => Option;
    sortOptions?: boolean;
    trailingButtons?: Component;
    autoChooseIfOnlyOneOption?: boolean;
    groupBy?: (option: Option) => string;
    fieldStyles?: CSSProperties;
    style?: CSSProperties;
};

const AxleAutocompleteMultiselect = <Option, InputValue extends AllowedInputValueItem>({
    label,
    description = undefined,
    inlineLabel = false,
    enabled = true,
    inputState,
    options,
    moreOptions = [],
    optionToValue,
    optionToLabel,
    optionToToolTip = () => '',
    renderOption = undefined,
    // Whether to sort the options.
    sortOptions = true,
    ChipComponent,
    ChipsContainerComponent,
    trailingButtons = null,
    autoChooseIfOnlyOneOption = false,
    groupBy = undefined,
    fieldStyles = undefined,
    style = {},
}: AxleAutocompleteMultiselectProps<Option, InputValue> & {
    ChipComponent: ({
        value,
        label,
        tooltip,
        onDelete,
    }: {
        value: InputValue;
        label: string;
        tooltip: string;
        onDelete: () => void;
    }) => Component;
    ChipsContainerComponent: ({ children }: { children: Children }) => Component;
}): ReturnComponent => {
    // Sort options by label, as a convenience to the caller.
    if (sortOptions) {
        options = _.sortBy(options, optionToLabel);
    }

    const fullOptionsArray = [...options, ...moreOptions];

    useEffect(() => {
        // If the options change, remove values no longer in the options.
        if (
            !inputState.value.every((selectedValue) =>
                includesObject(fullOptionsArray.map(optionToValue), selectedValue),
            )
        ) {
            inputState.setInputState(
                inputState.value.filter((value) =>
                    includesObject(fullOptionsArray.map(optionToValue), value),
                ),
            );
        }

        // If only one option, and autoChooseIfOnlyOneOption is true, set it.
        if (autoChooseIfOnlyOneOption && fullOptionsArray.length === 1) {
            setInputStateIfNotAlready(inputState, [optionToValue(fullOptionsArray[0])]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullOptionsArray, inputState]);

    return (
        <FieldWithAxleLabel
            label={label}
            description={description}
            inlineLabel={inlineLabel}
            required={inputState.required}
            sx={{ width: '100%' }}
        >
            <HorizontalStack>
                <AxleAutocompleteField
                    label={inlineLabel ? label : ''}
                    inlineLabel={inlineLabel}
                    enabled={enabled}
                    inputState={
                        {
                            ...inputState,
                            value: '',
                            setInputState: (value: InputValue) => {
                                // Sometimes the value seems to the empty string, which seems
                                //  to screw things up, so bail out in those cases.
                                if (value === '') return;
                                inputState.setInputState(arrayWithValue(inputState.value, value));
                            },
                            // We don’t want the <AxleAutocompleteField/> to show the helper text;
                            //  we’ll do it ourselves in this component.
                            helperText: '',
                            // TODO: This is a very aggressive type assertion; try to avoid.
                        } as unknown as InputState<InputValue>
                    }
                    options={options}
                    moreOptions={moreOptions}
                    optionToValue={optionToValue}
                    optionToLabel={optionToLabel}
                    renderOption={renderOption}
                    // No need for the <AxleAutocompleteField/> to sort; we handle it here.
                    sortOptions={false}
                    groupBy={groupBy}
                    specialAutocompleteOnlyToAddMode={true}
                    filterOptions={(options) =>
                        options.filter(
                            (option) => !includesObject(inputState.value, optionToValue(option)),
                        )
                    }
                    style={{
                        flexGrow: 1,
                        ...style,
                    }}
                    _autocompleteRootInputStyles={fieldStyles}
                />
                <If condition={!!trailingButtons}>{trailingButtons}</If>
            </HorizontalStack>
            <Box
                // Only add padding if there are any chips.
                mt={inputState.value.length > 0 ? 1 : 0}
            >
                <ChipsContainerComponent>
                    {fullOptionsArray.map((option) => (
                        <If
                            key={JSON.stringify(optionToValue(option))}
                            condition={includesObject(inputState.value, optionToValue(option))}
                        >
                            <ChipComponent
                                label={optionToLabel(option)}
                                tooltip={optionToToolTip(option)}
                                value={optionToValue(option)}
                                onDelete={() => {
                                    inputState.setInputState(
                                        arrayWithoutValue(inputState.value, optionToValue(option)),
                                    );
                                }}
                            />
                        </If>
                    ))}
                </ChipsContainerComponent>
            </Box>
            <FormHelperText error={inputState.showError}>{inputState.helperText}</FormHelperText>
        </FieldWithAxleLabel>
    );
};

export const AxleAutocompleteMultiselectWithShortChips = <
    Option,
    InputValue extends AllowedInputValueItem,
>({
    chipIcon = undefined,
    ...props
}: AxleAutocompleteMultiselectProps<Option, InputValue> & {
    chipIcon?: DefiniteComponent;
}) => {
    return (
        <AxleAutocompleteMultiselect
            {...props}
            ChipComponent={({ label, onDelete, tooltip }) => (
                <AxleChip
                    icon={chipIcon}
                    label={label}
                    tooltip={tooltip}
                    // Only hide if enabled is explicitly set to false. enabled as undefined or true are shown
                    onAction={props.enabled !== false ? onDelete : undefined}
                />
            )}
            ChipsContainerComponent={ChipsInRows}
        />
    );
};

export const AxleAutocompleteMultiselectWithFullWidthChips = <
    Option,
    InputValue extends AllowedInputValueItem,
>({
    chipIcon = undefined,
    ...props
}: AxleAutocompleteMultiselectProps<Option, InputValue> & {
    chipIcon?:
        | DefiniteComponent
        | (({
              value,
              label,
              tooltip,
              onDelete,
          }: {
              value: InputValue;
              label: string;
              tooltip: string;
              onDelete: () => void;
          }) => DefiniteComponent);
}) => {
    return (
        <AxleAutocompleteMultiselect
            {...props}
            ChipComponent={(params) => {
                const { label, tooltip, onDelete } = params;
                const icon = typeof chipIcon === 'function' ? chipIcon(params) : chipIcon;
                return (
                    <AxleChip
                        icon={icon}
                        label={label}
                        tooltip={tooltip}
                        // Only hide if enabled is explicitly set to false. enabled as undefined or true are shown
                        onAction={props.enabled !== false ? onDelete : undefined}
                        fullWidth={true}
                    />
                );
            }}
            ChipsContainerComponent={({ children }) => (
                <VerticalStack spacing={1}>{children}</VerticalStack>
            )}
        />
    );
};
